import type {
  JobStatus,
  PackshotJobCard,
  QaResult,
  StatusCounts,
} from '@/lib/packshots';
import { JOB_STATUSES } from '@/lib/packshots';
import { STAGE_LABEL, type StageId } from './railModel';

const STATUS_RANK: Record<JobStatus, number> = {
  needs_review: 0,
  failed: 1,
  awaiting_pick: 2,
  no_source_found: 3,
  processing: 4,
  sourcing: 4,
  queued: 4,
  completed: 5,
};

export function isInFlight(status: JobStatus): boolean {
  return status === 'queued' || status === 'processing' || status === 'sourcing';
}

export function statusGlyph(status: JobStatus): string {
  switch (status) {
    case 'needs_review':
      return '⚠';
    case 'completed':
      return '✓';
    case 'failed':
      return '✕';
    case 'awaiting_pick':
      return '◔';
    case 'no_source_found':
      return '⌕';
    case 'queued':
    case 'processing':
    case 'sourcing':
      return '◌';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

export function statusHeadline(status: JobStatus): string {
  switch (status) {
    case 'queued':
    case 'processing':
      return 'Processing…';
    case 'sourcing':
      return 'Searching the web…';
    case 'awaiting_pick':
      return 'Pick a source';
    case 'needs_review':
      return 'Needs a look';
    case 'completed':
      return 'Done';
    case 'failed':
      return 'Failed';
    case 'no_source_found':
      return 'Nothing suitable found';
    default: {
      const unreachable: never = status;
      return unreachable;
    }
  }
}

function isStageId(stage: string): stage is StageId {
  return stage in STAGE_LABEL;
}

export function progressLabel(job: { status: JobStatus; stage: string | null }): string | null {
  if (job.status === 'queued') return 'Queued';
  if (job.status === 'sourcing') return 'Searching the web…';
  if (job.status !== 'processing') return null;
  if (job.stage && isStageId(job.stage)) return STAGE_LABEL[job.stage];
  return 'Processing…';
}

export function isServiceBusyError(message: string | null | undefined): boolean {
  if (!message) return false;
  return /\b429\b|too many requests|throttl|image service is busy|image service is still busy/i.test(message);
}

export function userFacingError(message: string): string {
  if (isServiceBusyError(message)) return 'The image service is busy. This step will try again shortly.';
  return message;
}

export function jobDisplayName(job: Pick<PackshotJobCard, 'productName' | 'originalName' | 'productQuery'>): string {
  const named = job.productName?.trim();
  if (named) return named;
  const fallback = job.originalName?.trim() || job.productQuery?.trim();
  return fallback || 'Untitled';
}

export function compareJobs(a: PackshotJobCard, b: PackshotJobCard): number {
  const rank = STATUS_RANK[a.status] - STATUS_RANK[b.status];
  if (rank !== 0) return rank;
  return jobDisplayName(a).localeCompare(jobDisplayName(b));
}

export function totalImages(counts: StatusCounts): number {
  return JOB_STATUSES.reduce((sum, status) => sum + counts[status], 0);
}

export function inFlightCount(counts: StatusCounts): number {
  return counts.queued + counts.processing + counts.sourcing;
}

export function countsFromJobs(jobs: readonly { status: JobStatus }[]): StatusCounts {
  const counts: StatusCounts = {
    queued: 0,
    processing: 0,
    sourcing: 0,
    awaiting_pick: 0,
    needs_review: 0,
    completed: 0,
    failed: 0,
    no_source_found: 0,
  };
  for (const job of jobs) counts[job.status] += 1;
  return counts;
}

export function batchStatusPhrase(counts: StatusCounts): string {
  if (counts.needs_review > 0) {
    const count = counts.needs_review;
    return `⚠ ${count} ${count === 1 ? 'needs a look' : 'need a look'}`;
  }
  if (counts.failed > 0) return `✕ ${counts.failed} failed`;
  if (counts.awaiting_pick > 0) return `◔ ${counts.awaiting_pick} waiting for your pick`;
  if (counts.no_source_found > 0) return `⌕ ${counts.no_source_found} with nothing found`;
  if (inFlightCount(counts) > 0) return `◌ ${inFlightCount(counts)} processing`;
  if (totalImages(counts) === 0) return 'No images';
  return '✓ done';
}

export function qaStatusLabel(status: JobStatus, qa: QaResult | null | undefined): string {
  if (status === 'failed') return 'Failed';
  if (status === 'needs_review') return 'Needs a look';
  if (status === 'completed') return qa ? 'Passed' : 'Done';
  if (qa) return 'Checked';
  return 'Not run yet';
}
